import { LOCALISE_LANGUAGE_FIELD_NAME } from "@pkg/config/sanity/constants";
import { DOCUMENT, OBJECT } from "@pkg/config/sanity/schemas";
import groq from "groq";
import { slugWithParents } from "./slugWithParents";
import { THEME } from '@pkg/config/misc/theme';
import { LOCALE_CODE } from '@pkg/config/languages/localeCodes';
import type { CmsIconName } from '@pkg/cms-icons';
import type { Image as SanityImage } from "@sanity/types";
import { INTERNAL_LINK_TYPES } from '@pkg/config/sanity/schemaConfig';

export interface ExternalLink {
  _type: OBJECT.NAVIGATION_LINK_EXTERNAL;
  _key: string;
  title: string;
  url: string;
  newWindow: boolean;
}

export interface SSULink {
  _type: OBJECT.NAVIGATION_LINK_SSU;
  _key: string;
  title: string;
  url: string;
  newWindow: boolean;
}

// _type == '${OBJECT.NAVIGATION_LINK_EXTERNAL}'
export const externalLink = groq`
  ...
`;

// _type == '${OBJECT.NAVIGATION_LINK_SSU}'
export const ssuLink = groq`
  _type == '${OBJECT.NAVIGATION_LINK_SSU}' => {
  ...
}
`;

export interface InternalLinkReference {
  _type: string;
  slug: string;
  locale: string;
  /** Published audience taxonomy id (`audience-www` | `audience-business` | `audience-common`). */
  audienceRef?: string | null;
}

export const internalLinkReference = groq`
  _type,
  slug,
  "locale": ${LOCALISE_LANGUAGE_FIELD_NAME},
  "audienceRef": audience._ref,
  ${slugWithParents}
`;

export interface InternalLink {
  _type: OBJECT.NAVIGATION_LINK_INTERNAL;
  _key: string;
  title: string;
  newWindow: boolean;
  reference?: InternalLinkReference;
  internalAnchor?: string;
}

export const internalLink = groq`
_type == '${OBJECT.NAVIGATION_LINK_INTERNAL}' => {
  ...,
  newWindow,
  reference->{${internalLinkReference}}
}
`;

export interface SocialLink {
  _type: OBJECT.NAVIGATION_LINK_SOCIAL;
  _key: string;
  title: string;
  url: string;
  icon: string;
}

export const socialLink = groq`
  _type == '${OBJECT.NAVIGATION_LINK_SOCIAL}' => {
    _type,
    _key,
    title,
    url,
    icon,
  }
`;

export enum LinkFieldType {
  INTERNAL = 'internal',
  EXTERNAL = 'external',
  SSU = 'ssu',
}

export type GeneralLink = {
  linkType: LinkFieldType.INTERNAL;
  reference?: InternalLinkReference;
  internalAnchor?: string;
} | {
  linkType: LinkFieldType.EXTERNAL;
  external: string;
} | {
  linkType: LinkFieldType.SSU;
  ssu?: string;
};

export const generalLink = groq`
  ...,
  linkType == 'internal' => {
    'internalAnchor': internalAnchor.internal,
    'reference': internal->{
      ${internalLinkReference},
    }
  }
`;

export const PageReferenceTypes = INTERNAL_LINK_TYPES.map(page  => page.type);

export type PageReferenceType = typeof PageReferenceTypes[number];

export interface PageReference {
  _type: PageReferenceType;
  _key?: string;
  slug: string;
  locale: LOCALE_CODE;
  audienceRef?: string | null;
  internalAnchor?: string;
}

export interface CardLink {
  _key: string
  title: string;
  description: string;
  reference: PageReference,
  linkText?: {
    label: string;
    text: string;
  }
}

export const cardLink = groq`
  _type == '${OBJECT.NAVIGATION_LINK_CARD}' => {
    ...,
    reference->{
      _type,
      "locale": language,
      "audienceRef": audience._ref,
      ${slugWithParents}
    }
  }
`;

/*
  If we do not convert the "arbitrary" values to any's,
  the type will become incompatible with the useSiteManifestQuery
*/
type Image = Pick<SanityImage, "asset" | 'hotspot' | 'crop'> & {
  [key: string]: any;
}

export interface DropdownCta {
  title: string;
  theme: THEME;
  content: string;
  image: Image & { alt?: string };
  link: InternalLink | ExternalLink | SSULink;
}

export const dropdownCTA = groq`
  _type == '${OBJECT.NAVIGATION_DROPDOWN_CTA}' => {
    ...,
    link[0]{
      ${externalLink},
      ${internalLink},
      ${ssuLink},
    }
  }
`;

export interface IconLink {
  _key: string;
  title: string;
  description: string;
  icon: CmsIconName;
  reference?: {
    _type: string;
    slug: string;
    locale: LOCALE_CODE;
    audienceRef?: string | null;
  }
}

export const iconLink = groq`
  _type == '${OBJECT.NAVIGATION_LINK_ICON}' => {
    ...,
    reference->{
      _type,
      "locale": language,
      "audienceRef": audience._ref,
      ${slugWithParents}
    }
  }
`;
